import classNames from 'classnames'
import { ChangeEvent } from 'react'
import useProductVariant from '../helpers/useProductVariant'
import { hasOwnProperties } from '../helpers/utils'
import { typeVariant } from '../types'
import { type_WISHLIST_DASHBOARD_STATE } from '../types'

const colourKeys = ['Colours', 'Colors']

type props = {
  variants: Array<typeVariant>
  options: any[]
  updateCurrentVariant: any
  currentVariant: any
  displayType?: string
  hasOnlyDefaultVariant?: boolean
  hasPair?: boolean
  disableGroupTopPadding?: boolean
}
declare var wishlistDashboardState: type_WISHLIST_DASHBOARD_STATE

function ProductVariant({
  variants,
  options,
  updateCurrentVariant,
  currentVariant,
  displayType,
  hasOnlyDefaultVariant = false,
  hasPair = false,
  disableGroupTopPadding = false
}: props) {
  const isOnlyVariantDefault = hasOnlyDefaultVariant || (variants.length === 1 && variants[0].title === 'Default Title')

  if (isOnlyVariantDefault) {
    return <></>
  }

  if (variants.length === 1 && hasPair) {
    let temp = { ...variants[0] }
    temp.option2 = 'Matched Pair'
    temp.options = [temp.option1, temp.option2]
    variants.push(temp)
  }

  const { availableValues, selectableValues, selected, setSelected } = useProductVariant({ variants, options, updateCurrentVariant, currentVariant })

  if (hasPair) {
    availableValues['Style'].reverse()
  }

  return (
    <div className="flex flex-col">
      {Object.entries(availableValues).map(([key, values], index) => (
        <VariantGroup
          key={index}
          name={key}
          values={values}
          selected={selected}
          selectableValues={selectableValues}
          setSelected={setSelected}
          displayType={displayType}
          disableGroupTopPadding={disableGroupTopPadding}
        />
      ))}
    </div>
  )
}

type groupProps = {
  name: string
  values: unknown[]
  selected: any
  selectableValues: any
  setSelected: any
  displayType?: string
  disableGroupTopPadding?: boolean
}
function VariantGroup({ name, values, selected, selectableValues, setSelected, displayType, disableGroupTopPadding = false }: groupProps) {
  return (
    values.length > 1 && (
      <div
        className={classNames(
          'colors-swatch-wrapper',
          colourKeys.includes(name) && 'order-first',
          !disableGroupTopPadding && (name.toLowerCase() === 'letter' ? 'pt-[45px] lg-max:pt-10' : 'pt-4 first:pt-0')
        )}
      >
        {displayType === 'dropdown' ? (
          <VariantDropdown items={values} option={name} selected={selected} setSelected={setSelected} />
        ) : (
          <>
            <span className="colors-swatch__label">
              {name.toLowerCase() === 'length' ? wishlistDashboardState.product.size : `${name}:`}
            </span>
            <VariantList
              items={values}
              option={name}
              selected={selected}
              selectableValues={selectableValues}
              setSelected={setSelected}
              disableTopMargin={disableGroupTopPadding}
            />
          </>
        )}
      </div>
    )
  )
}

type itemProps = {
  className: string
  value: string
  option: string
  selected: any
  selectableValues: any
  setSelected: any
}

function VariantItem({ className, value, option, selected, selectableValues, setSelected }: itemProps) {
  const onSelect = (value: string, option: string) => {
    selected = { ...selected, [option]: value }
    setSelected(selected)
  }

  const isSelected = hasOwnProperties(option, selected) && selected[option] === value

  const isDisabled = hasOwnProperties(option, selectableValues) && !selectableValues[option].includes(value)

  const isColour = colourKeys.includes(option)

  const getSwatchClass = (name: string) => {
    return `colour-swatch-${name.toLowerCase()}`
  }

  return !isColour ? (
    <li className={classNames('colors-swatch__item', isSelected && 'colors-swatch__item--active', className)}>
      <button type="button" name={value} onClick={() => onSelect(value, option)}>
        {value.replace('Letter ', '')}
      </button>
    </li>
  ) : (
    <button
      type="button"
      className={`variant-item relative mt-2 h-12 w-12 rounded-full border-[3px] border-white outline outline-1 outline-transparent ${getSwatchClass(value)} ${
        isSelected && 'outline-default'
      } ${isDisabled && 'before:absolute before:left-0 before:h-[1px] before:w-full before:rotate-[135deg] before:bg-white before:content-[""]'}`}
      name={value}
      onClick={() => onSelect(value, option)}
      aria-label={getSwatchClass(value)}
    ></button>
  )
}

type listProps = {
  items: any[]
  option: string
  selected: any
  selectableValues: any
  setSelected: any
  disableTopMargin?: boolean
}

function VariantList({ items, option, selected, selectableValues, setSelected, disableTopMargin = false }: listProps) {
  return (
    <ul className={classNames('colors-swatch__list', option.toLowerCase() === 'letter' && !disableTopMargin && 'mt-[15px] lg:mt-5')}>
      {items.length &&
        items.map((item) => (
          <VariantItem
            key={`${option}-${item}`}
            className={option.toLowerCase() === 'letter' ? 'min-w-[30px]' : ''}
            value={item}
            option={option}
            selected={selected}
            selectableValues={selectableValues}
            setSelected={setSelected}
          />
        ))}
    </ul>
  )
}

type dropdownProps = {
  items: any[]
  option: string
  selected: any
  setSelected: any
}

function VariantDropdown({ items, option, selected, setSelected }: dropdownProps) {
  const onChange = (e: ChangeEvent<HTMLSelectElement>) => {
    selected = { ...selected, [option]: e.target.value }
    setSelected(selected)
  }

  return (
    items.length && (
      <select onChange={onChange} className="input !py-2.5 !text-deep-grey">
        {items.map((item) => (
          <option value={item} selected={selected[option] === item}>
            {item}
          </option>
        ))}
      </select>
    )
  )
}

export default ProductVariant
